/**
 * "Is hiring on for this project, may this user see it, may they manage it, and are
 * hiring requests theirs to see?"
 *
 * Only the server can say (the project's switch, and a per-role grant checked on a
 * verified sign-in), so it is asked — once per person and project, shared by every
 * screen that shows hiring: the Stores tab's column, the Hiring tab and the tab strip
 * that offers it.
 *
 * DARK UNTIL THE SERVER SAYS YES. The server itself is on from the day Hiring is
 * deployed; what it answers "no" for is a project whose switch a super admin has not
 * turned on (every project, to begin with) and a person without the grant. Three
 * answers, never two: `unknown` (not answered yet), `yes` and `no`. `canView`,
 * `canManage`, `canRaise` and `canSeeRequests` are false in every state but a `yes` that grants them, so a screen that
 * renders before the answer arrives shows nothing of hiring, and so does one whose
 * question failed for any reason at all — the project switched off or Hiring stopped on
 * the server (404), a sign-in the server could not verify (403), a network error, a
 * mocked api module with no such export. Nothing here ever shows a message.
 *
 * `canSeeRequests` is the server's word that hiring requests (one person asks for hiring
 * to be raised, another confirms) exist for this person here: Raise is on for the
 * project and on the server, and the person holds the Stores tick or "Raise & cancel
 * hiring". It only says whether to ASK for them: what the person may do with one is said
 * by the list of requests itself. A server that does not say it means no.
 *
 * `canRaise` is the server's word that this person holds "Raise & cancel hiring" (a row of
 * its own in Access Management, since 14 Oct 2026) or is a super admin: the screens then
 * offer "Raise hiring", and "Cancel hiring in Recruitment" where Cancel is on; everyone else
 * with the Stores tick gets "Ask for hiring" instead. It says nothing of the switches - the
 * list says whether Raise or Cancel is on. A server that does not say it means no.
 *
 * WHAT IS KEPT, for {@link HIRING_ACCESS_TTL_MS}: a 200 (whatever it grants) and the
 * two definite refusals (404 not enabled, 403 no access). Anything else reads as `no`
 * for the screens that asked and is asked again by the next one to mount. A screen
 * already on show keeps the answer it was given; the minute only decides whether the
 * NEXT screen to open asks again.
 *
 * ONE STORE, WITH SUBSCRIBERS. When any hiring call is refused as switched off, the
 * screen that saw it calls {@link forgetHiringAccess}: every screen showing hiring for
 * that project goes back to today's look at once, without a reload. Answers are kept
 * per PERSON and project, so someone who signs in after another person in the same
 * browser tab is never drawn the first person's column.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { hiringLinksAPI, type HiringAccessAnswer } from '../../services/api';
import { HIRING_CODES, hiringCodeOf, hiringStatusOf } from './hiringText';

/** The same minute the server keeps its own grant lookup for. */
export const HIRING_ACCESS_TTL_MS = 60 * 1000;

export interface HiringAccess {
  state: 'unknown' | 'yes' | 'no';
  canView: boolean;
  canManage: boolean;
  /** Holds "Raise & cancel hiring": raises, confirms or declines hiring requests, and cancels. False unless the server says so in plain words. */
  canRaise: boolean;
  /** Hiring requests are this person's to see in this project. False unless the server says so in plain words. */
  canSeeRequests: boolean;
}

const UNKNOWN: HiringAccess = { state: 'unknown', canView: false, canManage: false, canRaise: false, canSeeRequests: false };
const NO: HiringAccess = { state: 'no', canView: false, canManage: false, canRaise: false, canSeeRequests: false };

type Kept = { access: HiringAccess; expiresAt: number };

const kept = new Map<string, Kept>();
const inFlight = new Map<string, Promise<HiringAccess | null>>();
const listeners = new Set<() => void>();
// Bumped when answers are dropped, so the screens on show ask again.
let generation = 0;

const keyOf = (projectId: number, userKey: string): string => `${userKey}|${projectId}`;

const notify = (): void => {
  for (const listener of [...listeners]) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The kept answer if it was still fresh at `at`; null when absent or older. */
const readKept = (key: string, at: number): HiringAccess | null => {
  const hit = kept.get(key);
  return hit && hit.expiresAt > at ? hit.access : null;
};

const keep = (key: string, access: HiringAccess): void => {
  kept.set(key, { access, expiresAt: Date.now() + HIRING_ACCESS_TTL_MS });
};

const toAccess = (answer: HiringAccessAnswer | undefined): HiringAccess => {
  const canView = answer?.canView === true;
  // Managing includes seeing; an answer that grants one without the other grants neither.
  const canManage = canView && answer?.canManage === true;
  // Raising includes seeing; a server from before the row says nothing of it, which is no.
  const canRaise = canView && answer?.canRaise === true;
  // Seeing hiring requests includes seeing hiring: without that, there are none to see.
  const canSeeRequests = canView && answer?.canSeeRequests === true;
  return canView ? { state: 'yes', canView, canManage, canRaise, canSeeRequests } : NO;
};

/**
 * Who is signed in, for keeping answers apart. Any stable text will do; it is never
 * sent anywhere.
 */
export const hiringUserKeyOf = (
  user: { userName?: string | null; userCode?: number | string | null; id?: string | null } | null | undefined,
): string => String(user?.userName ?? user?.userCode ?? user?.id ?? '').trim().toLowerCase();

/**
 * The answer for one person and project: a kept one, or the server's. `null` = no usable
 * answer (nothing is kept, and the next caller asks again). Never rejects.
 */
export const fetchHiringAccess = (projectId: number, userKey = ''): Promise<HiringAccess | null> => {
  const key = keyOf(projectId, userKey);
  const fresh = readKept(key, Date.now());
  if (fresh) return Promise.resolve(fresh);

  const existing = inFlight.get(key);
  if (existing) return existing;

  const asked = generation;
  // Wrapped, not just `.catch`ed: a THROW while building the request (a stubbed api
  // module in a test, a bundle that failed to load) must read as "off", not take the
  // page down.
  const request: Promise<HiringAccess | null> = Promise.resolve()
    .then(() => hiringLinksAPI.access(projectId))
    .then(
      (res) => toAccess(res?.data?.data),
      (err: unknown) => {
        const status = hiringStatusOf(err);
        const code = hiringCodeOf(err);
        const definite =
          (status === 404 && code === HIRING_CODES.notEnabled) || (status === 403 && code === HIRING_CODES.noAccess);
        return definite ? NO : null;
      },
    )
    .then((access) => {
      // An answer that was asked for before the answers were dropped is not kept.
      if (access && asked === generation) {
        keep(key, access);
        notify();
      }
      return access;
    })
    .finally(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
};

export const useHiringAccess = (projectId: number | null | undefined, userKey = ''): HiringAccess => {
  const key = projectId ? keyOf(projectId, userKey) : '';
  const asks = useSyncExternalStore(subscribe, () => generation);
  // When this screen started looking at this person and project. An answer that was
  // fresh then, or arrived since, is this screen's for as long as it is on show.
  const looked = useRef<{ id: string; at: number } | null>(null);
  const lookId = `${key}#${asks}`;
  if (looked.current?.id !== lookId) looked.current = { id: lookId, at: Date.now() };
  const lookedAt = looked.current.at;
  const keptAccess = useSyncExternalStore(subscribe, () => (key ? readKept(key, lookedAt) : null));
  // The key this screen asked about and got no usable answer for.
  const [failedKey, setFailedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    setFailedKey(null);
    void fetchHiringAccess(projectId, userKey).then((access) => {
      if (!cancelled && access === null) setFailedKey(keyOf(projectId, userKey));
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, userKey, asks]);

  if (!key) return NO;
  if (keptAccess) return keptAccess;
  return failedKey === key ? NO : UNKNOWN;
};

/**
 * A hiring call was just refused as switched off or not allowed for this project: what
 * was known no longer holds. The project reads as `no` for everyone, for the next
 * minute — the answer the server itself would give — and every screen showing hiring
 * returns to today's look. Deliberately not "ask again now": a screen that asked again
 * on every refusal could ask for ever.
 */
export const forgetHiringAccess = (projectId: number | null | undefined): void => {
  if (!projectId) return;
  const suffix = `|${projectId}`;
  // Whoever is on the page, and whoever the answer was kept for.
  for (const key of [...kept.keys(), ...inFlight.keys()]) {
    if (key.endsWith(suffix)) keep(key, NO);
  }
  generation += 1;
  notify();
};

/** Drops every answer, so the screens on show ask again. After the switch is saved; and in tests. */
export const clearHiringAccessCache = (): void => {
  kept.clear();
  inFlight.clear();
  generation += 1;
  notify();
};
